import { useRef, useEffect, useState, useCallback, createRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl, navigate } from 'gatsby-plugin-intl';

import { PreventDefault } from '../../utils/events';
import { geocodeLocation, getBrowserCoords } from '../../utils/location';
import useScript from '../../utils/use-script';
import useBrowserLocation from '../../utils/use-browser-location';
import InputField from '../InputField';
import StoreInfoCard from '../StoreInfoCard';
import RichText from '../RichText';
import GoogleMarkerDefault from '../../images/icon-google-marker-default.svg';
import GoogleMarkerActive from '../../images/icon-google-marker-active.svg';
import IconDirections from '../../images/icon-directions.svg';

import { lightThemeOptions, darkThemeOptions } from './map-styles';
import {
  Section,
  SectionContainer,
  LocationPageTitle,
  Search,
  InputWrapper,
  SubmitButton,
  ClearButton,
  SearchResults,
  Summary,
  TabContainer,
  TabButton,
  ResultsRow,
  StoreListing,
  GoogleMap,
} from './styles';

const StoreMapSection = ({ data, autoLocate, themes }) => {
  const {
    locationPageHeading,
    searchBarLabel,
    searchButton,
    errorMessage,
    getDirectionsText,
    showingText,
    nearestResultsText,
    listText,
    mapText,
    storeLogos,
  } = data;

  let autocomplete;
  const intl = useIntl();
  const googleMapRef = useRef(null);
  const inputRef = createRef();
  const [map, setMap] = useState(null);
  const { queryParams } = useBrowserLocation();
  const searchTerm = queryParams.address || '';
  const [focus, setFocus] = useState(false);
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedMarkers, setSelectedMarkers] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState(false);

  // Mobile: Set active tab to the  matching ID
  const [viewId, setViewId] = useState('map');

  // Set empty array for markers that are selected on the map
  const markers = [];

  // Google Maps API
  const [mapScriptLoaded] = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${process.env.GATSBY_GOOGLE_MAPS_KEY}&language=${intl.locale}&region=CA&libraries=places`
  );

  // Gets the list of stores from the bullseye API
  const getStores = async () => {
    if (location) {
      const requestURL = `https://ws2.bullseyelocations.com/restsearch.svc/dosearch2?ClientID=${process.env.GATSBY_BULLSEYE_CLIENTID}&ApiKey=${process.env.GATSBY_BULLSEYE_APIKEY}&CountryID=2&CategoryIds=93189,93188,93192,93235,96250&StartIndex=0&PageSize=20&Latitude=${location.lat}&Longitude=${location.lng}&Radius=20`;

      try {
        const res = await fetch(requestURL);
        const { TotalResults, ResultList } = await res.json();

        if (TotalResults) {
          setError(false);
          setStores(ResultList);
        } else {
          setError(true);
          setStores([]);
        }
      } catch (e) {
        console.warn(e);
        setError(true);
        setStores([]);
      }
    }
  };

  // Determine which logo to use
  const searchTerms = [
    { search: 'sdm', logo: 'Shoppers Drug Mart' },
    { search: 't&t', logo: 'T&T' },
    { search: 'joe', logo: 'Loblaws' },
    { search: 'loblaws', logo: 'Loblaws' },
    { search: 'no frills', logo: 'No Frills' },
    {
      search: 'real canadian superstore',
      logo: 'Real Canadian Superstore',
    },
    { search: 'loblaw pharmacy', logo: 'Loblaws' },
    { search: 'zehrs', logo: 'Zehrs' },
    { search: 'independent city market', logo: 'City Market' },
    { search: 'bloor street market', logo: 'City Market' },
    { search: 'wholesale club', logo: 'Wholesale Club' },
    { search: 'valu-mart', logo: 'Valu-mart' },
    { search: 'provigo', logo: 'Provigo le Marché' },
    { search: 'maxi', logo: 'Maxi & cie' },
    { search: 'atlantic superstore', logo: 'Atlantic Superstore' },
    { search: 'your independent grocer', logo: 'Your Independent Grocer' },
    { search: 'extra foods', logo: 'Extra Foods' },
    { search: 'fortinos', logo: 'Fortinos' },
  ];

  // Update map based on users autocomplete selection
  const updateMap = (position) => {
    setLocation(position);
  };

  // Set search to selected autocomplete option
  const handlePlaceSelect = () => {
    const place = autocomplete.getPlace();
    if (place && place.geometry) {
      setError(false);
      findLocations(place.formatted_address);
      updateMap({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      });
    }
  };

  // If on locations page, scroll to map once item has been searched
  const scrollToMap = () => {
    if (window.location.href.indexOf('find') > -1) {
      const element = document.getElementById('map');
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition - headerOffset;

      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
          document.getElementById('map').focus();
        }, 300);
      }
    }
  };

  // If store selected om map, set selection to top of results list
  const scrollToStore = (storeId) => {
    const element = document.getElementById(storeId);
    const elementContainer = document.getElementById('storeListings');
    const elementPos = element.offsetTop;
    elementContainer.scrollTop = elementPos;
  };

  // Change query string in url to new location
  const findLocations = (customSearch) =>
    navigate(
      `/${searchButton.linkUrlMarkdown.linkUrlMarkdown}${
        customSearch || search
      }`
    );

  // Focus events for search input
  const focusOutsideOfSearch = useCallback((event) => {
    const { keyCode } = event;
    if (keyCode === 9) {
      if (event.target.classList.contains('searchable-marker')) {
        return;
      }
      setFocus(false);
    }
  }, []);

  const clickOutsideOfSearch = useCallback((event) => {
    if (event.target.classList.contains('searchable-marker')) {
      return;
    }
    setFocus(false);
  }, []);

  useEffect(() => {
    if (window) {
      window.addEventListener('keyup', focusOutsideOfSearch);
      window.addEventListener('click', clickOutsideOfSearch);
    }
    return () => {
      window.removeEventListener('keyup', focusOutsideOfSearch);
      window.removeEventListener('click', clickOutsideOfSearch);
    };
  }, [focusOutsideOfSearch]);

  // Check for "address" query string in url. If has value, set as search query
  useEffect(() => {
    if (window) {
      if (searchTerm || searchTerm !== null) {
        setSearch(searchTerm);
      }
    }
  }, []);

  // If location has been searched (current or previous page), scroll to map when page loads
  useEffect(() => {
    if (searchTerm && autoLocate) {
      scrollToMap();
    }
  }, [searchTerm]);

  // If location center map on location
  useEffect(() => {
    if (location) {
      if (map) map.setCenter(location);
      getStores();
    }
  }, [location]);

  // Everytime the user searchs in a different location, drop pins to identify each nearby location
  useEffect(() => {
    if (stores && stores.length) {
      // Remove old markers
      if (selectedMarkers) {
        selectedMarkers.forEach((marker) => marker.setMap(null));
      }

      // Create a bounds object to fit the new markers
      const bounds = new window.google.maps.LatLngBounds();

      // Create an info window to share between markers.
      const infoWindow = new window.google.maps.InfoWindow();

      // Create bounce effect on marker
      const toggleBounce = (marker) => {
        if (marker.getAnimation()) {
          marker.setAnimation(null);
        } else {
          marker.setAnimation(window.google.maps.Animation.BOUNCE);

          // Stop pin movement after 2 bounces
          setTimeout(() => {
            marker.setAnimation(null);
          }, 1000);
        }
      };

      // Set up markers for each store
      stores.forEach((store) => {
        const { Latitude: lat, Longitude: lng } = store;

        // Set logo for each store
        const categories = store.CategoryNames.toLowerCase();
        const foundStore = searchTerms.find(
          // eslint-disable-next-line
          ({ search }) => categories.indexOf(search) > 0
        );
        const logoKey = foundStore ? foundStore.logo : 'Loblaws';
        const foundLogo = storeLogos.find(
          ({ storeLogoDam }) => storeLogoDam.altText === logoKey
        );

        // Default marker pin
        const markerDefault = {
          url: GoogleMarkerDefault,
          scaledSize: new window.google.maps.Size(40, 40),
          origin: new window.google.maps.Point(0, 0),
          width: 50,
        };

        // Active marker pin
        const markerActive = {
          url: GoogleMarkerActive,
          scaledSize: new window.google.maps.Size(40, 40),
          origin: new window.google.maps.Point(0, 0),
        };

        // Create markers per store
        const marker = new window.google.maps.Marker({
          id: store.Id,
          position: { lat, lng },
          optimized: false,
          map,
          icon: markerDefault,
        });

        // Push selected markers to an array so when another is clicked, all current markers stop actions
        markers.push(marker);

        // Set loaded markers to marker list
        setSelectedMarkers(markers);

        // Set listener on marker to show information and bounce when clicked
        marker.addListener('click', (markerType) => {
          // Pan window smoothly to new item on next selection
          map.panTo(marker.getPosition());

          // clear active markers
          for (let i = 0; i < markers.length; i++) {
            markers[i].setAnimation(null);
            markers[i].setIcon(markerDefault);
          }
          marker.setIcon(markerActive);
          infoWindow.close();
          infoWindow.setContent(markerInfo(store, foundLogo, themes));
          infoWindow.open(marker.getMap(), marker);
          toggleBounce(marker);
          setSelectedStore(store);

          // Scroll to selected store in list
          // If selection is coming from results list, don't scroll results list
          if (markerType !== 'result') {
            scrollToStore(`store-${store.Id}`);
          }
        });

        // Stop bounce on marker once infoWindow close is clicked
        infoWindow.addListener('closeclick', () => {
          marker.setAnimation(null);
          marker.setIcon(markerDefault);
          setSelectedStore(null);
          setDrawerOpen(false);

          // Scroll back to the top of the store listings on close for desktop
          if (window.innerWidth > 768) {
            scrollToStore(`storeListings`);
          }
        });

        bounds.extend(marker.getPosition());

        return marker;
      });

      map.fitBounds(bounds);
      if (map.getZoom() <= 7) {
        const center = markers[0].getPosition();
        map.setCenter(center);
        map.setZoom(7);
      }
    }
  }, [stores]);

  // Enable google autocomplete for search when script has loaded
  useEffect(() => {
    const options = {
      types: ['geocode'],
      componentRestrictions: { country: 'ca' },
    };
    if (map) {
      autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        options
      );
      autocomplete.addListener('place_changed', handlePlaceSelect);
    }
  }, [map]);

  // If location search term set map to search
  useEffect(() => {
    if (searchTerm && map) {
      if (searchTerm.match(/^(my location|ma position)$/i)) {
        getBrowserCoords()
          .then(({ lat, lng }) => {
            setLocation({
              lat,
              lng,
            });
          })
          .catch((e) => console.warn(e));
      } else {
        geocodeLocation(searchTerm)
          .then((result) => {
            setLocation({
              lat: result.geometry.location.lat(),
              lng: result.geometry.location.lng(),
            });
          })
          .catch(console.warn);
      }
    }
  }, [searchTerm, map]);

  // Set map google script has loaded
  useEffect(() => {
    if (typeof document !== 'undefined' && window.google) {
      const mapOptions = {
        zoom: 12,
        center: { lat: 43.6185201, lng: -79.76889609999999 },
        styles: themes === 'dark' ? darkThemeOptions : lightThemeOptions,
      };
      const newMap = new window.google.maps.Map(
        googleMapRef.current,
        mapOptions
      );
      setMap(newMap);
    }
  }, [mapScriptLoaded, themes]);

  // Set layout for Google infoWindow dialog
  const markerInfo = (store, logo) => {
    const storeLocation = `https://www.google.com/maps/place/${store.Address1} ${store.City} ${store.State} ${store.PostCode}/`;
    const storeLocationFormatted = `${store.Address1}<br/>${store.City},&nbsp;${store.State} ${store.PostCode}`;
    const googleMapsUrl = storeLocation.split(' ').join('+');
    const ariaLabel = getDirectionsText || '';

    return `
      <div class="gm-style marker-wrapper">
        <div class="marker-logo-container">
          <div class="marker-logo">
            <img
              src=${
                themes === 'dark'
                  ? logo.storeLogoDarkModeDam.asset.url
                  : logo.storeLogoDam.asset.url
              }
              alt=${logo.storeLogoDam.altText}
            />
          </div>
          <div class="marker-store-distance">${store.Distance}km</div>
          </div>
            <div class="marker-group">
              <div class="marker-store-name">${store.Name}</div>
              <div class="marker-directions">
              <a 
                class="marker-directions-anchor" 
                target="_blank" 
                rel="noopener noreferrer"
                href=${googleMapsUrl} 
                aria-label=${ariaLabel}
                title=${ariaLabel}
              >
              <div class="marker-directions-address">
              ${storeLocationFormatted}
              </div>
              <div class="marker-directions-icon">
                <img
                  src=${IconDirections}
                  alt=""
                  role="presentation"
                />
              </div>
            </a>
          </div>
        </div>
      </div>
    `;
  };

  return (
    <>
      <Section>
        <SectionContainer>
          {locationPageHeading && (
            <LocationPageTitle>
              <RichText document={locationPageHeading} />
            </LocationPageTitle>
          )}
          <form
            onSubmit={PreventDefault(() => {
              findLocations();
            })}
          >
            <Search>
              <InputWrapper>
                <InputField
                  type="text"
                  id="address"
                  name="address"
                  label={searchBarLabel}
                  value={search}
                  onFocus={() => {
                    setFocus(true);
                  }}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  active={focus}
                  themes={themes}
                  ref={inputRef}
                />
                <SubmitButton
                  variant={searchButton.style}
                  type="submit"
                  ariaLabel={searchButton.ariaLabel}
                >
                  {searchButton.linkTextMarkdown.linkTextMarkdown}
                </SubmitButton>

                <p className="accessibility" role="status" aria-live="polite">
                  {search}
                </p>
                <ClearButton
                  onClick={() => {
                    setSearch('');
                    setError(true);
                  }}
                  themes={themes}
                >
                  X
                </ClearButton>
              </InputWrapper>
            </Search>
          </form>
        </SectionContainer>
      </Section>
      <SearchResults id="map">
        {(error || (!location && !searchTerm)) && (
          <Summary className="view-desktop error" themes={themes}>
            <RichText document={errorMessage} />
          </Summary>
        )}
        {showingText && nearestResultsText && (
          <Summary className="view-mobile">
            {error || (!location && !searchTerm) ? (
              <RichText document={errorMessage} />
            ) : (
              <p role="status">
                {showingText} {stores.length} {nearestResultsText} [&nbsp;
                {searchTerm}&nbsp;]
              </p>
            )}
          </Summary>
        )}
        <TabContainer role="tablist">
          {listText && (
            <TabButton
              role="tab"
              id="tab-0"
              tabIndex="0"
              aria-selected="false"
              aria-controls="storeListings"
              themes={themes}
              onClick={(e) => {
                e.preventDefault();
                setViewId('list');
              }}
              aria-label={`${listText} - ${
                intl.locale === 'en'
                  ? `open tabbed content`
                  : `ouvrir le contenu à onglets`
              }`}
              className={viewId === 'list' ? 'active' : null}
            >
              {listText}
            </TabButton>
          )}
          {mapText && (
            <TabButton
              role="tab"
              id="tab-1"
              tabIndex="0"
              aria-selected="true"
              aria-controls="google-map"
              themes={themes}
              onClick={(e) => {
                e.preventDefault();
                setViewId('map');
              }}
              aria-label={`${mapText} - ${
                intl.locale === 'en'
                  ? `open tabbed content`
                  : `ouvrir le contenu à onglets`
              }`}
              className={viewId === 'map' ? 'active' : null}
            >
              {mapText}
            </TabButton>
          )}
        </TabContainer>
        <ResultsRow>
          {stores.length ? (
            <StoreListing
              role="tabpanel"
              id="storeListings"
              tabIndex="-1"
              themes={themes}
              className={viewId === 'list' ? 'active' : null}
            >
              {error || (!location && !searchTerm) ? null : (
                <Summary id="storeListingsDesktop" className="view-desktop">
                  {showingText && nearestResultsText && (
                    <p role="status">
                      {showingText} {stores.length} {nearestResultsText} [&nbsp;
                      {searchTerm}&nbsp;]
                    </p>
                  )}
                </Summary>
              )}

              {stores.map((store) => {
                const marker =
                  selectedMarkers &&
                  selectedMarkers.find((m) => m.id === store.Id);

                return (
                  <StoreInfoCard
                    data={data}
                    id={`store-${store.Id}`}
                    key={store.ThirdPartyId}
                    searchTerms={searchTerms}
                    store={store}
                    logos={storeLogos}
                    setSelectedStore={setSelectedStore}
                    selectedStore={selectedStore && selectedStore.Id}
                    marker={marker}
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    themes={themes}
                  />
                );
              })}
            </StoreListing>
          ) : null}
          <GoogleMap
            role="tabpanel presentation"
            id="google-map"
            tabIndex="0"
            ref={googleMapRef}
            aria-hidden="true"
            themes={themes}
            className={viewId === 'map' ? 'active' : ''}
          />
        </ResultsRow>
      </SearchResults>
    </>
  );
};

StoreMapSection.propTypes = {
  data: PropTypes.shape().isRequired,
  autoLocate: PropTypes.bool,
  themes: PropTypes.string.isRequired,
};

StoreMapSection.defaultProps = {
  autoLocate: false,
};

export default StoreMapSection;
